import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  User,
  Bell,
  Palette,
  Shield,
  Loader2,
  Save,
  DollarSign,
  Copy,
  Check,
  Users,
  TrendingUp,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import DashboardLayout from "@/components/dashboard/DashboardLayout";

const SettingsPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState("");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [marketingEmails, setMarketingEmails] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
    if (user) {
      setFullName(user.user_metadata?.full_name || "");
    }
  }, [user, loading, navigate]);

  const handleSaveProfile = () => {
    toast.success("Profile settings saved!");
  };

  const referralCode = user?.id?.slice(0, 8)?.toUpperCase() || "XXXXXXXX";
  const referralLink = `https://thumbgeniusai.lovable.app/?ref=${referralCode}`;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success("Lien copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  // Simulated referral stats
  const referralStats = {
    referrals: 0,
    conversions: 0,
    earnings: "$0.00",
    potentialPerReferral: "$5.00",
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <DashboardLayout activeTab="settings">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center justify-between px-4 sm:px-6 py-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">Settings</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Manage your account preferences
              </p>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 max-w-2xl">
          {/* Profile Section */}
          <div className="p-6 rounded-xl bg-card border border-border mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <User className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Profile</h3>
                <p className="text-sm text-muted-foreground">Update your personal information</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  value={user.email || ""}
                  disabled
                  className="mt-1.5 opacity-60"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Email cannot be changed
                </p>
              </div>
              <Button onClick={handleSaveProfile}>
                <Save className="w-4 h-4" />
                Save Changes
              </Button>
            </div>
          </div>

          {/* Referral / Earn Money Section */}
          <div className="p-6 rounded-xl bg-gradient-to-br from-emerald-500/10 to-card border border-emerald-500/30 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-400">Gagnez de l'argent 💰</h3>
                <p className="text-sm text-muted-foreground">Parrainez des créateurs et gagnez des commissions</p>
              </div>
            </div>

            {/* Referral Link */}
            <div className="mb-6">
              <Label className="text-xs text-muted-foreground mb-1.5 block">Votre lien de parrainage</Label>
              <div className="flex gap-2">
                <Input
                  value={referralLink}
                  readOnly
                  className="font-mono text-xs bg-background/50 border-emerald-500/20"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 border-emerald-500/30 hover:bg-emerald-500/10"
                  onClick={handleCopyLink}
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* Earnings Dashboard */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-background/50 border border-emerald-500/10 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <Users className="w-4 h-4 text-emerald-400" />
                </div>
                <p className="text-lg font-bold text-emerald-400">{referralStats.referrals}</p>
                <p className="text-[10px] text-muted-foreground">Filleuls</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50 border border-emerald-500/10 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                </div>
                <p className="text-lg font-bold text-emerald-400">{referralStats.conversions}</p>
                <p className="text-[10px] text-muted-foreground">Conversions</p>
              </div>
              <div className="p-3 rounded-lg bg-background/50 border border-emerald-500/10 text-center">
                <div className="flex items-center justify-center gap-1 mb-1">
                  <DollarSign className="w-4 h-4 text-emerald-400" />
                </div>
                <p className="text-lg font-bold text-emerald-400">{referralStats.earnings}</p>
                <p className="text-[10px] text-muted-foreground">Gains</p>
              </div>
            </div>

            <p className="text-xs text-emerald-400/70 mt-4 text-center">
              Gagnez {referralStats.potentialPerReferral} pour chaque utilisateur parrainé qui passe Pro
            </p>
          </div>

          {/* Notifications Section */}
          <div className="p-6 rounded-xl bg-card border border-border mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Notifications</h3>
                <p className="text-sm text-muted-foreground">Configure how you receive updates</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Email Notifications</p>
                  <p className="text-sm text-muted-foreground">
                    Receive updates about your thumbnails
                  </p>
                </div>
                <Switch
                  checked={emailNotifications}
                  onCheckedChange={setEmailNotifications}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Marketing Emails</p>
                  <p className="text-sm text-muted-foreground">
                    Receive tips and product updates
                  </p>
                </div>
                <Switch
                  checked={marketingEmails}
                  onCheckedChange={setMarketingEmails}
                />
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="p-6 rounded-xl bg-card border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold">Security</h3>
                <p className="text-sm text-muted-foreground">Manage your account security</p>
              </div>
            </div>

            <Button variant="outline">Change Password</Button>
          </div>
        </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
